import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { Exclude, Expose } from "class-transformer";
import { IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanCentralStation extends FloorplanProductItem<CentralStationConfiguration> {
  @Exclude()
  private readonly _centralStation: CentralStationConfiguration;

  @IsString()
  @Expose({ name: "centralStationId" })
  private readonly _centralStationId: string;

  constructor(
    floorplan: Floorplan,
    centralStationConfiguration: CentralStationConfiguration,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, centralStationConfiguration, id, x, y, size, notes);
    this._centralStationId = centralStationConfiguration?.id;
    this._centralStation = centralStationConfiguration;
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.centralStationsItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanCentralStation) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => FloorplanCentralStation.create(this._floorplan, config, x, y, size);
  }

  override delete() {
    this._floorplan.deleteCentralStation(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.centralStations;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanCentralStation,
        this._floorplan.centralStations,
        FloorplanEventType.CENTRAL_STATION_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        afterDuplicateAdded as ((duplicate: FloorplanCentralStation) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshCentralStationsLock();
  }

  protected override getConfigFromProject(floorplan: Floorplan): CentralStationConfiguration | undefined {
    return floorplan.getCentralStationConfig(this._centralStationId);
  }

  static create(
    floorplan: Floorplan,
    centralStation: CentralStationConfiguration,
    x: number,
    y: number,
    size: number,
  ): FloorplanCentralStation {
    return new FloorplanCentralStation(floorplan, centralStation, uuidv4(), x, y, size);
  }
}
